import React from "react";

import { faCaretDown, faCaretUp } from "@fortawesome/pro-solid-svg-icons";

import { AvatarProfile, ButtonIcon, InputNumeric, Text } from "@performyard/py-components";
import type { ICellRendererParams } from "@performyard/py-components";
import { toFixed } from "@performyard/utils-isomorphic";

import type { DecisionRow } from "../../types";
import { DecisionTableContext } from "../MyDecisions/types";

import { getRoleOrJobTitleDisplay } from "../../../../utils/roleJobTitleDisplay";
import { formatConditionalDecimals, formatCurrencyFull } from "../../utils";
import { isDraftAmountMissing, isDraftPercentMissing } from "./utils";

import { useIncreaseValue, useIsEditingRow } from "../MyDecisions/decision-store";

/** Cycle `allotment_increase_percent` surfaced as grid context `recommendedIncreasePercent` — display only. */
export const SuggestedIncreaseCellRenderer = (params: ICellRendererParams<DecisionRow>) => {
	const row = params.data;
	const context = params.context as DecisionTableContext | undefined;
	if (!row || !context || row.superRow) {
		return null;
	}
	const label = `${formatConditionalDecimals(context.recommendedIncreasePercent)}%`;
	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3" weight="semi-bold" color="green-5">
				{label}
			</Text>
		</div>
	);
};

export const EmployeeNameCellRenderer = (params: ICellRendererParams<DecisionRow>) => {
	const row = params.data;
	const context = params.context as DecisionTableContext;
	if (!row) return null;
	const isExpanded = context.expandedRowId === row?._id;

	const handleToggle = () => {
		if (isExpanded) {
			context.onCollapse(row._id);
		} else {
			context.onExpand(row._id);
		}
	};

	const secondaryDisplay = getRoleOrJobTitleDisplay({
		roleValue: row.employee_role,
		jobTitleValue: row.employee_title,
		canViewRole: context.canViewRole,
		canViewJobTitle: context.canViewJobTitle,
	});

	return (
		<div className="flex h-full items-center gap-2">
			<ButtonIcon
				variant="ghost"
				color="neutral"
				size={16}
				icon={isExpanded ? faCaretUp : faCaretDown}
				onClick={(e) => {
					e.stopPropagation();
					handleToggle();
				}}
				aria-label={isExpanded ? "Collapse Notes" : "Expand Notes"}
			/>
			<div className="flex min-w-0 flex-col justify-center gap-0.5">
				<Text
					size="paragraph-3"
					weight="regular"
					color="neutral-9"
					wrap={false}
					dangerouslySetClassName="truncate"
				>
					{row.employee_name}
				</Text>
				{secondaryDisplay.kind !== "none" && (
					<Text size="caption" weight="regular" color="neutral-7">
						{secondaryDisplay.value}
					</Text>
				)}
			</div>
		</div>
	);
};

export const CurrentSalaryCellRenderer = (params: ICellRendererParams<DecisionRow>) => {
	const row = params.data;
	if (!row) return null;
	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3" weight="regular">
				{formatCurrencyFull(row.current_salary)}
			</Text>
		</div>
	);
};

export const IncreasePercentCellRenderer = (params: ICellRendererParams<DecisionRow>): React.ReactNode => {
	const row = params.data;
	const context = params.context as DecisionTableContext | undefined;
	const storeValue = useIncreaseValue(context?.store, row?._id ?? "");
	const storeIsEditing = useIsEditingRow(context?.store, row?._id ?? "");
	if (!row || !context) return null;

	const rawContextValue = (storeValue ?? context.increaseValues[row._id])?.percent;
	const displayValue: number | null = typeof rawContextValue === "number" ? toFixed(rawContextValue, 2) : null;
	const rowStatus = row.status || "pending";
	const isInlineEditing = context.store ? storeIsEditing : context.editingRowIds?.has(row._id) === true;

	const showValidationError =
		!context.readOnlyBudgetReview &&
		!isInlineEditing &&
		rowStatus === "pending" &&
		!row.superRow &&
		String(context.groupCannotBeSubmitted ?? "") === String(context.managerId) &&
		isDraftPercentMissing(context.increaseValues, row._id);

	if (!isInlineEditing && (context.readOnlyBudgetReview || rowStatus !== "pending")) {
		return row.proposed_increase_percent != null ? (
			<div className="flex h-full items-center">
				<Text size="paragraph-3" weight="regular">
					{formatConditionalDecimals(row.proposed_increase_percent)}%
				</Text>
			</div>
		) : (
			<div className="flex h-full items-center">
				<Text size="paragraph-3" weight="regular" color="neutral-6">
					—
				</Text>
			</div>
		);
	}

	return (
		<div
			className="flex h-full items-center gap-1"
			onKeyDown={(e) => e.stopPropagation()}
			onKeyUp={(e) => e.stopPropagation()}
		>
			<InputNumeric
				id={`increase-percent-${row._id}`}
				data-testid={`increase-percent-${row._id}`}
				value={displayValue}
				allowEmpty={true}
				onChange={(val) => {
					if (val == null) {
						context.onPercentChange(row._id, "");
						return;
					}
					context.onPercentChange(row._id, val);
				}}
				onFocus={() => {
					if (displayValue == null) {
						context.onPercentChange(row._id, context.recommendedIncreasePercent);
					}
				}}
				precision={2}
				step={1}
				placeholder="-"
				errored={showValidationError}
				dangerouslySetClassName="w-[82px]"
				size={"small"}
			/>
			<span className="text-sm text-neutral-500">%</span>
		</div>
	);
};

export const IncreaseAmountCellRenderer = (params: ICellRendererParams<DecisionRow>): React.ReactNode => {
	const row = params.data;
	const context = params.context as DecisionTableContext | undefined;
	const storeValue = useIncreaseValue(context?.store, row?._id ?? "");
	const storeIsEditing = useIsEditingRow(context?.store, row?._id ?? "");
	if (!row || !context) return null;

	const rawContextValue = (storeValue ?? context.increaseValues[row._id])?.amount;
	const displayValue: number | null = typeof rawContextValue === "number" ? toFixed(rawContextValue, 2) : null;
	const rowStatus = row.status || "pending";
	const isInlineEditing = context.store ? storeIsEditing : context.editingRowIds?.has(row._id) === true;

	const showValidationError =
		!context.readOnlyBudgetReview &&
		!isInlineEditing &&
		rowStatus === "pending" &&
		!row.superRow &&
		String(context.groupCannotBeSubmitted ?? "") === String(context.managerId) &&
		isDraftAmountMissing(context.increaseValues, row._id);

	if (!isInlineEditing && (context.readOnlyBudgetReview || rowStatus !== "pending")) {
		return row.proposed_increase_amount != null ? (
			<div className="flex h-full items-center">
				<Text size="paragraph-3" weight="regular">
					{formatCurrencyFull(row.proposed_increase_amount)}
				</Text>
			</div>
		) : (
			<div className="flex h-full items-center">
				<Text size="paragraph-3" weight="regular" color="neutral-6">
					—
				</Text>
			</div>
		);
	}

	return (
		<div
			className="flex h-full items-center gap-1"
			onKeyDown={(e) => e.stopPropagation()}
			onKeyUp={(e) => e.stopPropagation()}
		>
			<span className="text-sm text-neutral-500">$</span>
			<InputNumeric
				id={`increase-amount-${row._id}`}
				data-testid={`increase-amount-${row._id}`}
				value={displayValue}
				allowEmpty={true}
				onChange={(val) => {
					if (val == null) {
						context.onAmountChange(row._id, "");
						return;
					}
					context.onAmountChange(row._id, val);
				}}
				onFocus={() => {
					if (displayValue == null) {
						context.onPercentChange(row._id, context.recommendedIncreasePercent);
					}
				}}
				precision={2}
				step={1}
				placeholder="-"
				errored={showValidationError}
				dangerouslySetClassName="w-[82px]"
				size={"small"}
			/>
		</div>
	);
};

export const NewSalaryCellRenderer = (params: ICellRendererParams<DecisionRow>) => {
	const row = params.data;
	const context = params.context as DecisionTableContext;
	const storeValue = useIncreaseValue(context?.store, row?._id ?? "");
	if (!row || !context) return null;
	const increaseAmount = (storeValue ?? context.increaseValues[row._id])?.amount;
	const hasValue = increaseAmount != null && increaseAmount !== "";
	const newSalary = hasValue ? row.current_salary + (increaseAmount as number) : null;
	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3" weight="regular" color={newSalary != null ? undefined : "neutral-6"}>
				{newSalary != null ? formatCurrencyFull(newSalary) : "—"}
			</Text>
		</div>
	);
};

export const EmployeeAvatarCellRenderer = (params: ICellRendererParams<DecisionRow>): React.ReactNode => {
	const row = params.data;
	if (!row || row.superRow) {
		return null;
	}

	return (
		<div className="flex h-full min-w-0 items-center">
			<AvatarProfile employeeId={row.employee_id} employeeName={row.employee_name ?? ""} noLink={true} />
		</div>
	);
};
